/**
 * HMW renderer (artifact_name: `how_might_we`).
 * JSON: { source_context, hmws: HMWStep[3-5] } where each block has
 * { question, description, ideas: [{ description } x2] }.
 */

import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@ideoz/ui';
import { CardShell, dirOf } from './shared/components';
import {
  parseSourceContext,
  sourceContextToHtml,
  sourceContextToJson,
  sourceContextToMarkdown,
  SourceContextCard,
  type SourceContextModel,
} from './shared/sourceContext';
import { esc, htmlDoc } from './shared/html';
import type { ArtifactRenderer } from './types';

interface HmwBlock {
  question: string;
  description: string;
  ideas: string[];
}
export interface HmwModel {
  source: SourceContextModel;
  blocks: HmwBlock[];
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

// -- parse / serialize -------------------------------------------------------

export function parseHmw(json: Record<string, unknown>): HmwModel | null {
  if (!Array.isArray(json.hmws)) return null;
  const blocks = json.hmws.filter(isRecord).map((h) => ({
    question: str(h.question),
    description: str(h.description),
    ideas: Array.isArray(h.ideas) ? h.ideas.filter(isRecord).map((i) => str(i.description)) : [],
  }));
  if (blocks.length === 0) return null;
  return { source: parseSourceContext(json.source_context), blocks };
}

export function hmwToJson(model: HmwModel): Record<string, unknown> {
  return {
    source_context: sourceContextToJson(model.source),
    hmws: model.blocks.map((b, i) => ({
      hmw_number: i + 1,
      question: b.question,
      description: b.description,
      ideas: b.ideas.map((d, j) => ({ idea_number: j + 1, description: d })),
    })),
  };
}

export function hmwToMarkdown(model: HmwModel): string {
  const parts = [sourceContextToMarkdown(model.source), '## HMW Questions'];
  model.blocks.forEach((b) => {
    const lines = [`**${b.question}**`];
    if (b.description) lines.push(`_${b.description}_`);
    b.ideas.filter(Boolean).forEach((idea) => lines.push(`- **Idea:** ${idea}`));
    parts.push(lines.join('\n'));
  });
  return parts.join('\n\n');
}

// -- card --------------------------------------------------------------------

const HmwCard: React.FC<{ model: HmwModel }> = ({ model }) => (
  <CardShell testId="hmw-artifact-card">
    <SourceContextCard model={model.source} />
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
      <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        HMW Questions
      </h3>
      <ol className="space-y-5">
        {model.blocks.map((b, i) => (
          <li key={i}>
            <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              HMW {i + 1}
            </div>
            <div
              dir={dirOf(b.question)}
              className="text-start text-sm font-semibold text-gray-900 dark:text-gray-100"
            >
              {b.question}
            </div>
            {b.description && (
              <p
                dir={dirOf(b.description)}
                className="mt-0.5 text-start text-sm leading-relaxed text-gray-600 dark:text-gray-400"
              >
                {b.description}
              </p>
            )}
            {b.ideas.length > 0 && (
              <ul className="mt-1.5 space-y-1.5">
                {b.ideas.map((idea, j) => (
                  <li
                    key={j}
                    dir={dirOf(idea)}
                    className="text-start text-sm leading-relaxed text-gray-700 dark:text-gray-300"
                  >
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      Idea {j + 1}:
                    </span>{' '}
                    {idea}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  </CardShell>
);

// -- editor ------------------------------------------------------------------

const inputCls =
  'w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';

const HmwEditor: React.FC<{
  model: HmwModel;
  onReady: (getJson: () => Record<string, unknown>) => void;
}> = ({ model, onReady }) => {
  const [state, setState] = React.useState<HmwModel>(model);
  const stateRef = React.useRef(state);
  stateRef.current = state;

  React.useEffect(() => {
    onReady(() => hmwToJson(stateRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutate = (fn: (d: HmwModel) => HmwModel) => setState((p) => fn(structuredClone(p)));

  return (
    <CardShell>
      {state.blocks.map((b, bi) => (
        <div
          key={bi}
          className="flex flex-col gap-2 rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              HMW {bi + 1}
            </span>
            <button
              type="button"
              onClick={() => mutate((d) => ({ ...d, blocks: d.blocks.filter((_, j) => j !== bi) }))}
              className="text-gray-400 hover:text-red-500"
              aria-label="Remove HMW"
            >
              <Trash2 size={13} />
            </button>
          </div>
          <textarea
            value={b.question}
            rows={2}
            placeholder="How might we …?"
            onChange={(e) =>
              mutate((d) => {
                d.blocks[bi].question = e.target.value;
                return d;
              })
            }
            className={inputCls}
          />
          <textarea
            value={b.description}
            rows={2}
            placeholder="Why this HMW (connection to the problem)"
            onChange={(e) =>
              mutate((d) => {
                d.blocks[bi].description = e.target.value;
                return d;
              })
            }
            className={inputCls}
          />
          {b.ideas.map((idea, ii) => (
            <div key={ii} className="flex items-center gap-2">
              <input
                value={idea}
                placeholder={`Idea ${ii + 1}`}
                onChange={(e) =>
                  mutate((d) => {
                    d.blocks[bi].ideas[ii] = e.target.value;
                    return d;
                  })
                }
                className={inputCls}
              />
              <button
                type="button"
                onClick={() =>
                  mutate((d) => {
                    d.blocks[bi].ideas.splice(ii, 1);
                    return d;
                  })
                }
                className="text-gray-400 hover:text-red-500"
                aria-label="Remove idea"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="gap-1 self-start"
            onClick={() =>
              mutate((d) => {
                d.blocks[bi].ideas.push('');
                return d;
              })
            }
          >
            <Plus size={12} /> Add idea
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="xs"
        className="gap-1 self-start"
        onClick={() =>
          mutate((d) => ({
            ...d,
            blocks: [...d.blocks, { question: '', description: '', ideas: [''] }],
          }))
        }
      >
        <Plus size={12} /> Add HMW
      </Button>
    </CardShell>
  );
};

// -- html --------------------------------------------------------------------

export function hmwToHtml(model: HmwModel, title = 'How Might We'): string {
  const blocks = model.blocks
    .map(
      (
        b,
        i
      ) => `<li><div class="step-label">HMW ${i + 1}</div><div class="q">${esc(b.question)}</div>
      ${b.description ? `<p class="a">${esc(b.description)}</p>` : ''}
      ${b.ideas.length ? `<ul>${b.ideas.map((idea, j) => `<li><b>Idea ${j + 1}:</b> ${esc(idea)}</li>`).join('')}</ul>` : ''}</li>`
    )
    .join('');
  const body = `${sourceContextToHtml(model.source)}
    <section class="card"><h3>HMW Questions</h3><ol>${blocks}</ol></section>`;
  return htmlDoc(title, body);
}

export const hmwRenderer: ArtifactRenderer<HmwModel> = {
  type: 'how_might_we',
  matchNames: ['how_might_we'],
  parse: parseHmw,
  toJson: hmwToJson,
  toMarkdown: hmwToMarkdown,
  toHtml: hmwToHtml,
  Card: HmwCard,
  Editor: HmwEditor,
};
